import {
  PasswordProtection,
  CustomizedBlockPage,
  IFlagsConfig,
  SERVER_SYNCED_FLAG_KEYS,
  CONSENT_FLAG_KEYS,
  FLAG_CONFIG_KEYS,
  Group,
  SaveUserConfigPayload,
} from '@/models';
import {
  groupsInitialState,
  passwordProtectionInitialState,
  customizedBlockPageInitialState,
  flagsConfigInitialState,
} from '@/store/reducers';
import { isEqual, pick } from 'lodash';

interface LocalConfigState {
  readonly groups: Group[];
  readonly passwordProtection: PasswordProtection;
  readonly customizedBlockPage: CustomizedBlockPage;
  readonly flagsConfig: IFlagsConfig;
}

/**
 * Compares current Redux state against initial values to identify
 * changes made by an anonymous user that should be preserved after login.
 * Returns only SERVER_SYNCED_FLAG_KEYS for flagsConfig.
 */
export const getLocalConfigChangesFromDefault = (
  currentState: LocalConfigState
): SaveUserConfigPayload | undefined => {
  const changes: SaveUserConfigPayload = {};
  let hasChanges = false;

  if (currentState.groups.length > groupsInitialState.length) {
    changes.groups = currentState.groups;
    hasChanges = true;
  }

  if (
    !isEqual(currentState.passwordProtection, passwordProtectionInitialState)
  ) {
    changes.passwordProtection = currentState.passwordProtection;
    hasChanges = true;
  }

  if (
    !isEqual(currentState.customizedBlockPage, customizedBlockPageInitialState)
  ) {
    changes.customizedBlockPage = currentState.customizedBlockPage;
    hasChanges = true;
  }

  const currentSyncedFlags = pick(
    currentState.flagsConfig,
    SERVER_SYNCED_FLAG_KEYS
  );
  const defaultSyncedFlags = pick(
    flagsConfigInitialState,
    SERVER_SYNCED_FLAG_KEYS
  );

  if (!isEqual(currentSyncedFlags, defaultSyncedFlags)) {
    changes.flagsConfig = currentSyncedFlags;
    hasChanges = true;
  }

  return hasChanges ? changes : undefined;
};

/**
 * The consent flags to carry across a login. Returns only keys that are actually
 * set, so we never write an empty bucket array or a false sticky flag.
 */
export const getConsentSnapshot = (
  flagsConfig: IFlagsConfig
): Partial<IFlagsConfig> => {
  const snapshot: Partial<IFlagsConfig> = {};
  CONSENT_FLAG_KEYS.forEach(key => {
    const value = flagsConfig[key];
    if (key === FLAG_CONFIG_KEYS.OPT_IN_PREFERENCES) {
      if (Array.isArray(value) && value.length > 0) {
        (snapshot as Record<string, unknown>)[key] = value;
      }
    } else if (value === true) {
      (snapshot as Record<string, unknown>)[key] = true;
    }
  });
  return snapshot;
};

/**
 * Union of an anonymous consent snapshot with the account's server consent, used
 * only on login. Buckets are unioned so the account keeps its own buckets plus the
 * ones granted anonymously; sticky flags are OR-ed. Returns only keys that end up set.
 */
export const mergeConsentFlags = (
  local: Partial<IFlagsConfig>,
  remote?: IFlagsConfig
): Partial<IFlagsConfig> => {
  const merged: Partial<IFlagsConfig> = {};

  const unionBuckets = Array.from(
    new Set([
      ...(remote?.optInPreferences ?? []),
      ...(local.optInPreferences ?? []),
    ])
  );
  if (unionBuckets.length > 0) {
    merged.optInPreferences = unionBuckets;
  }

  CONSENT_FLAG_KEYS.forEach(key => {
    if (key === FLAG_CONFIG_KEYS.OPT_IN_PREFERENCES) {
      return;
    }
    if (local[key] === true || remote?.[key] === true) {
      (merged as Record<string, unknown>)[key] = true;
    }
  });

  return merged;
};

/**
 * Consent reset used on logout. Resets only the flags the user currently holds,
 * as an explicit false (the payload is merged over the still-logged-in flags, so
 * an omitted key keeps the stale true). optInPreferences empties only on the
 * buckets schema; sending [] to a legacy user would fabricate the array and
 * migrate them off the old schema.
 */
export const getClearedConsentFlags = (
  currentFlags: IFlagsConfig
): Partial<IFlagsConfig> => {
  const cleared: Partial<IFlagsConfig> = {};
  const isOnBucketsSchema =
    currentFlags.onNewOptInSchema === true ||
    Array.isArray(currentFlags.optInPreferences);
  if (isOnBucketsSchema) {
    cleared.optInPreferences = [];
  }
  CONSENT_FLAG_KEYS.forEach(key => {
    if (key === FLAG_CONFIG_KEYS.OPT_IN_PREFERENCES) {
      return;
    }
    if (currentFlags[key] === true) {
      (cleared as Record<string, unknown>)[key] = false;
    }
  });
  return cleared;
};
